// Obliczenia fizyczne i termiczne dla procesu gięcia tworzyw sztucznych
// Bazuje na równaniach przewodnictwa cieplnego i właściwościach materiałów

import { 
  BendingMaterial, 
  BendingMachine, 
  BendingParameters, 
  BendingResult,
  BendingDefect,
  BENDING_DEFECTS,
  findMaterial,
  findMachine 
} from './BendingDatabase';

/**
 * Oblicza parametry gięcia na podstawie danych wejściowych
 */
export class BendingPhysics {
  
  /**
   * Główna funkcja obliczająca parametry gięcia
   */
  static calculateBendingParameters(params: BendingParameters): BendingResult {
    const material = findMaterial(params.materialId);
    const machine = findMachine(params.machineId);
    
    if (!material || !machine) {
      throw new Error('Nie znaleziono materiału lub maszyny');
    }
    
    // Podstawowe obliczenia temperatur
    const temps = this.calculateHeaterTemperatures(material, params);
    
    // Obliczenie czasu nagrzewania
    const heatingTime = this.calculateHeatingTime(
      material, 
      params.thickness, 
      params.humidity,
      params.ambientTemp
    );
    
    // Obliczenie szerokości strefy grzania
    const heatingZoneWidth = this.calculateHeatingZoneWidth(
      params.thickness,
      params.bendRadius,
      machine.defaultHeatingWidth
    );
    
    // Obliczenie czasu chłodzenia
    const coolingTime = this.calculateCoolingTime(
      material,
      params.thickness,
      params.bendAngle
    );
    
    // Obliczenie kompensacji sprężynowania
    const springBackCompensation = this.calculateSpringBack(
      material,
      params.bendAngle,
      params.thickness
    );
    
    // Sprawdzenie ostrzeżeń
    const warnings = this.checkWarnings(
      material,
      temps,
      heatingTime,
      params
    );
    
    // Generowanie rekomendacji
    const recommendations = this.generateRecommendations(
      material,
      params,
      warnings
    );
    
    // Całkowity czas cyklu
    const estimatedCycleTime = heatingTime + coolingTime + 10; // +10s na manipulację
    
    return {
      upperHeaterTemp: Math.round(temps.upper),
      lowerHeaterTemp: Math.round(temps.lower),
      heatingTime: Math.round(heatingTime),
      heatingZoneWidth: Math.round(heatingZoneWidth),
      coolingTime: Math.round(coolingTime),
      springBackCompensation: Math.round(springBackCompensation * 10) / 10,
      estimatedCycleTime: Math.round(estimatedCycleTime),
      warnings,
      recommendations
    };
  }
  
  /**
   * Oblicza temperatury grzałek górnej i dolnej
   */
  private static calculateHeaterTemperatures(
    material: BendingMaterial, 
    params: BendingParameters
  ): { upper: number; lower: number } {
    // Temperatura bazowa - środek zakresu temperatury szklistej
    const baseTemp = (material.glassTempMin + material.glassTempMax) / 2;
    
    // Korekta ze względu na grubość
    const thicknessCorrection = params.thickness > 5 
      ? (params.thickness - 5) * 2 
      : 0;
    
    // Korekta ze względu na kąt gięcia
    const angleCorrection = params.bendAngle > 90 
      ? (params.bendAngle - 90) * 0.15 
      : 0;
    
    // Korekta ze względu na promień gięcia
    const radiusCorrection = params.bendRadius < 2 
      ? 5 // Ostre gięcie wymaga wyższej temperatury
      : -2; // Łagodne gięcie
    
    // Korekta ze względu na temperaturę otoczenia
    const ambientCorrection = (20 - params.ambientTemp) * 0.5;
    
    // Temperatura górnej grzałki (główna)
    let upperTemp = baseTemp + thicknessCorrection + angleCorrection + 
                    radiusCorrection + ambientCorrection + 15;
    
    // Temperatura dolnej grzałki (wspomagająca, nieco niższa)
    let lowerTemp = upperTemp - (5 + params.thickness * 0.5);
    
    // Ograniczenia temperatury
    upperTemp = Math.min(upperTemp, material.maxBendingTemp);
    upperTemp = Math.max(upperTemp, material.glassTempMin);
    lowerTemp = Math.min(lowerTemp, material.maxBendingTemp - 10);
    lowerTemp = Math.max(lowerTemp, material.glassTempMin - 10);
    
    return { upper: upperTemp, lower: lowerTemp };
  }
  
  /**
   * Oblicza czas nagrzewania materiału
   */
  private static calculateHeatingTime(
    material: BendingMaterial,
    thickness: number,
    humidity: number,
    ambientTemp: number
  ): number {
    // Podstawowy czas = grubość × współczynnik materiału
    let heatingTime = thickness * material.bendingCoefficient;
    
    // Korekta ze względu na wilgotność (dla materiałów wrażliwych)
    if (material.moistureSensitive && humidity > 60) {
      heatingTime *= 1 + (humidity - 60) * 0.005; // +0.5% na każde 1% wilgoci
    }
    
    // Korekta ze względu na temperaturę otoczenia
    const tempCorrection = (20 - ambientTemp) * 0.02; // 2% na każdy stopień
    heatingTime *= (1 + tempCorrection);
    
    // Dodatkowy czas dla grubych materiałów (>10mm)
    if (thickness > 10) {
      heatingTime += (thickness - 10) * 5;
    }
    
    // Minimalny czas nagrzewania
    heatingTime = Math.max(heatingTime, 15);
    
    return heatingTime;
  }
  
  /**
   * Oblicza szerokość strefy nagrzewania
   */
  private static calculateHeatingZoneWidth(
    thickness: number,
    bendRadius: number,
    defaultWidth: number
  ): number {
    // Bazowa szerokość = 3 × grubość materiału
    let zoneWidth = thickness * 3;
    
    // Korekta ze względu na promień gięcia
    if (bendRadius > 5) {
      zoneWidth += bendRadius * 0.5;
    }
    
    // Minimalna szerokość to domyślna szerokość maszyny
    zoneWidth = Math.max(zoneWidth, defaultWidth);
    
    // Maksymalna szerokość to 10 × grubość
    zoneWidth = Math.min(zoneWidth, thickness * 10);
    
    return zoneWidth;
  }
  
  /**
   * Oblicza czas chłodzenia/stabilizacji
   */
  private static calculateCoolingTime(
    material: BendingMaterial,
    thickness: number,
    bendAngle: number
  ): number {
    // Bazowy czas chłodzenia
    let coolingTime = thickness * 10 / material.coolingRate;
    
    // Korekta ze względu na kąt gięcia
    if (bendAngle > 90) {
      coolingTime *= 1 + (bendAngle - 90) / 180;
    }
    
    // Minimalny czas chłodzenia
    coolingTime = Math.max(coolingTime, 20);
    
    // Dla grubych materiałów wydłuż czas
    if (thickness > 8) {
      coolingTime += (thickness - 8) * 5;
    }
    
    return coolingTime;
  }
  
  /**
   * Oblicza kompensację sprężynowania (spring-back)
   */
  private static calculateSpringBack(
    material: BendingMaterial,
    bendAngle: number,
    thickness: number
  ): number {
    // Bazowa kompensacja = kąt × współczynnik sprężynowania
    let compensation = bendAngle * material.springBackFactor;
    
    // Korekta ze względu na grubość
    const thicknessCorrection = thickness > 5 
      ? (thickness - 5) * 0.02 
      : 0;
    
    compensation *= (1 + thicknessCorrection);
    
    // Maksymalna kompensacja to 15% kąta
    compensation = Math.min(compensation, bendAngle * 0.15);
    
    return compensation;
  }
  
  /**
   * Sprawdza potencjalne problemy i generuje ostrzeżenia
   */
  private static checkWarnings(
    material: BendingMaterial,
    temperatures: { upper: number; lower: number },
    heatingTime: number,
    params: BendingParameters
  ): string[] {
    const warnings: string[] = [];
    
    // Sprawdzenie temperatury
    if (temperatures.upper > material.warningTemp) {
      warnings.push(`⚠️ Wysoka temperatura (${temperatures.upper}°C) - ryzyko białych smug lub przebarwień`);
    }
    
    if (temperatures.upper < material.glassTempMin) {
      warnings.push(`⚠️ Niska temperatura - ryzyko pęknięć na linii gięcia`);
    }
    
    // Sprawdzenie wilgotności
    if (material.moistureSensitive && params.humidity > 70) {
      warnings.push(`⚠️ Wysoka wilgotność (${params.humidity}%) - zalecane suszenie materiału przed gięciem`);
    }
    
    // Sprawdzenie długości gięcia
    const machine = findMachine(params.machineId);
    if (machine && params.bendLength > machine.maxLength) {
      warnings.push(`⚠️ Długość gięcia (${params.bendLength}mm) przekracza możliwości maszyny (max ${machine.maxLength}mm)`);
    }
    
    // Sprawdzenie grubości
    if (params.thickness > 15) {
      warnings.push(`⚠️ Gruba płyta (${params.thickness}mm) - proces może wymagać kilku cykli grzania`);
    }
    
    // Sprawdzenie ostrych gięć
    if (params.bendRadius < 1 && params.bendAngle > 90) {
      warnings.push(`⚠️ Ostre gięcie pod dużym kątem - zwiększone ryzyko pęknięć`);
    }
    
    // Sprawdzenie czasu
    if (heatingTime > 180) {
      warnings.push(`⚠️ Długi czas nagrzewania (${Math.round(heatingTime)}s) - sprawdź parametry lub rozważ wstępne podgrzanie`);
    }
    
    return warnings;
  }
  
  /**
   * Generuje rekomendacje dla operatora
   */
  private static generateRecommendations(
    material: BendingMaterial,
    params: BendingParameters,
    warnings: string[]
  ): string[] {
    const recommendations: string[] = [];
    
    // Rekomendacje dla wilgoci
    if (material.moistureSensitive && params.humidity > 60) {
      recommendations.push(
        `💡 Zalecane suszenie materiału: 80°C przez ${Math.round(params.thickness * 0.5)}h przed gięciem`
      );
    }
    
    // Rekomendacje dla grubych materiałów
    if (params.thickness > 10) {
      recommendations.push(
        `💡 Dla grubości ${params.thickness}mm rozważ dwustopniowe nagrzewanie`
      );
    }
    
    // Rekomendacje dla dużych kątów
    if (params.bendAngle > 120) {
      recommendations.push(
        `💡 Dla kąta ${params.bendAngle}° wykonuj gięcie powoli i równomiernie`
      );
    }
    
    // Rekomendacje dla ostrych gięć
    if (params.bendRadius < 2) {
      recommendations.push(
        `💡 Ostre gięcie - użyj taśmy maskującej do ograniczenia strefy grzania`
      );
    }
    
    // Rekomendacje dla typu materiału
    if (material.type === 'PMMA_GS') {
      recommendations.push(
        `💡 Plexi GS - nagrzewaj równomiernie, unikaj lokalnych przegrzań`
      );
    } else if (material.type === 'PC') {
      recommendations.push(
        `💡 Poliwęglan - materiał sztywny, gięcie wykonuj zdecydowanie`
      );
    } else if (material.type === 'PETG') {
      recommendations.push(
        `💡 PETG - łatwy w gięciu, uważaj na przegrzanie`
      );
    } else if (material.type === 'PVC_FOAM') {
      recommendations.push(
        `💡 PVC spienione - delikatny materiał, używaj niskich temperatur`
      );
    }
    
    // Rekomendacje dla małych promieni
    if (params.bendRadius > 10) {
      recommendations.push(
        `💡 Duży promień gięcia - rozważ użycie formy lub szablonu`
      );
    }
    
    // Jeśli nie ma ostrzeżeń
    if (warnings.length === 0) {
      recommendations.push(
        `✅ Parametry optymalne - postępuj zgodnie z wyliczonymi wartościami`
      );
    }
    
    return recommendations;
  }
  
  /**
   * Analiza defektu i sugestie naprawy
   */
  static analyzeDefect(defectName: string, currentParams: BendingResult): {
    defect: BendingDefect | undefined;
    suggestedParams: Partial<BendingResult>;
  } {
    const defect = BENDING_DEFECTS.find(d => 
      d.name.toLowerCase().includes(defectName.toLowerCase())
    );
    
    if (!defect) {
      return { defect: undefined, suggestedParams: {} };
    }
    
    const suggestedParams: Partial<BendingResult> = {};
    
    // Sugestie na podstawie typu defektu
    if (defect.preventionTemp?.max && currentParams.upperHeaterTemp > defect.preventionTemp.max) {
      suggestedParams.upperHeaterTemp = defect.preventionTemp.max - 5;
      suggestedParams.lowerHeaterTemp = defect.preventionTemp.max - 10;
    }
    
    if (defect.preventionTemp?.min && currentParams.upperHeaterTemp < defect.preventionTemp.min) {
      suggestedParams.upperHeaterTemp = defect.preventionTemp.min + 5;
      suggestedParams.lowerHeaterTemp = defect.preventionTemp.min;
    }
    
    if (defect.preventionTime?.min && currentParams.heatingTime < defect.preventionTime.min) {
      suggestedParams.heatingTime = defect.preventionTime.min;
    }
    
    return { defect, suggestedParams };
  }
  
  /**
   * Oblicza rozkład temperatury w materiale (dla wizualizacji)
   */
  static calculateTemperatureDistribution(
    material: BendingMaterial,
    thickness: number,
    upperTemp: number,
    lowerTemp: number,
    heatingTime: number
  ): number[] {
    const layers = 10; // Liczba warstw do symulacji
    const distribution: number[] = [];
    
    // Uproszczony model przewodnictwa cieplnego
    const alpha = material.thermalConductivity / (material.densityKgM3 * material.specificHeat);
    const dt = heatingTime / 10; // krok czasowy
    const dx = thickness / layers; // grubość warstwy
    
    // Inicjalizacja temperatury (temperatura otoczenia)
    for (let i = 0; i <= layers; i++) {
      distribution.push(20);
    }
    
    // Symulacja nagrzewania (uproszczona)
    for (let t = 0; t < 10; t++) {
      const newDist = [...distribution];
      
      // Warunki brzegowe
      newDist[0] = upperTemp * (t / 10); // Górna powierzchnia
      newDist[layers] = lowerTemp * (t / 10); // Dolna powierzchnia
      
      // Równanie przewodnictwa dla warstw wewnętrznych
      for (let i = 1; i < layers; i++) {
        const d2T = distribution[i + 1] - 2 * distribution[i] + distribution[i - 1];
        newDist[i] = distribution[i] + alpha * dt * d2T / (dx * dx);
      }
      
      distribution.splice(0, distribution.length, ...newDist);
    }
    
    return distribution.map(t => Math.round(t));
  }
  
  /**
   * Szacuje zużycie energii dla procesu gięcia
   */
  static estimateEnergyConsumption(
    heatingTime: number,
    upperTemp: number,
    lowerTemp: number,
    bendLength: number
  ): number {
    // Zakładamy moc grzałek 50W na cm długości
    const heaterPower = 0.05; // kW/cm
    const lengthInCm = bendLength / 10;
    
    // Moc proporcjonalna do temperatury (uproszczenie)
    const upperPower = heaterPower * lengthInCm * (upperTemp / 200);
    const lowerPower = heaterPower * lengthInCm * (lowerTemp / 200);
    
    // Energia = moc × czas
    const energy = (upperPower + lowerPower) * (heatingTime / 3600); // kWh
    
    return Math.round(energy * 1000) / 1000; // Zaokrąglij do 3 miejsc
  }
}